import { entryMapKey } from "@/lib/entry-key";
// Config-driven renderer for any multi-record section on layoutType
// "expandable". A compact basic-field row per record expands (independently /
// multi-expand) into the full record: grouped fields, Notes, and Documents.
// Div-grid (no <table>) so it never reproduces the <div>-in-<tbody> warning and
// never horizontally scrolls. Per-section copy + field layout come from
// EXPANDABLE_LAYOUT_CONFIG (section-layout-config.ts); currently adopted by the
// Vehicle List section.
//
// All data/save/document plumbing is REUSED from GenericCategoryPage via props;
// this component owns only expand state and presentation.

import { useEffect, useRef, useState, type ReactNode } from "react";
import { ArrowRight, ChevronDown, ChevronRight, Download, Eye, EyeOff, FileText, Info, Loader2, Lock, Paperclip, Plus, RotateCcw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EditableCell } from "@/components/EditableCell";
import { SendToPhoneDialog } from "@/components/SendToPhoneDialog";
import { HeaderOverContent } from "@/components/HeaderOverContent";
import { lensFieldAnchor, lensColumnAnchor } from "@/lib/planner-lens-ui";
import {
  deriveCellInputType,
  type BasicFieldWidth,
  type DrawerFieldSpan,
  type ExpandableLayoutConfig,
} from "@shared/constants/section-layout-config";
import { isCategoryAField } from "@/lib/data-classification";
import { evaluateShowIf, type ShowIfCondition, type ShowIfEntryMap } from "@/lib/evaluate-show-if";
import { DOCUMENT_ACCEPT } from "@/lib/upload-accept";
import { RequiredStar } from "@/components/RequiredFieldStar";
import { FieldDeclineControl } from "@/components/FieldDeclineControl";
import { type DeclineReason } from "@shared/constants/decline-reason";
import { needsDeclineDrawerHome } from "@shared/lib/decline-home";
import { fieldRendersStar } from "@shared/lib/field-star";
import { SpecifyOnOtherInput } from "@/components/SpecifyOnOtherInput";
import { isOtherOption } from "@shared/constants/specify-on-other";
import { headerRowId, isHeaderRow, isSeededDefaultRow } from "@shared/constants/locked-rows";
import { sectionHasEssentialRowTie, isEssentialTiedRow } from "@shared/essentials-spec";
import { isTopZoneScaffold, topZoneCurationFieldKey, TOP_ZONE_SEEDED_STARTER_SECTIONS } from "@shared/constants/top-zone-scaffolds";
import { isLockedIdentityField } from "@/lib/section-data-preview";
import { editedFieldIdsOnRow } from "@/lib/executor-plan-edit";
import { outstandingRequiredInRow, type SectionProgressField } from "@shared/section-progress";
import { isSeverityOptionalField } from "@shared/constants/severity-optional-fields";
import type { GenericField } from "@shared/schema";
import { SAGE, WARM } from "@shared/brand";

interface SectionWithFields {
  id: string;
  slug: string;
  name: string;
  fields: GenericField[];
}

export interface PanelDoc {
  id: string;
  filename: string;
  fileSize?: number | null;
  mimeType?: string | null;
  uploadedAt?: string | null;
  /** s96: AI summary persisted on the document (extractedMetadata.summary).
   *  s217: `documentType` rides beside it so rule 120's will-only planner gate can be applied
   *  here — the panel has no other way to know what kind of paper the document is. */
  extractedMetadata?: { summary?: string | null; documentType?: string | null } | null;
}

export interface ExpandableSectionLayoutProps {
  section: SectionWithFields;
  config: ExpandableLayoutConfig;
  /** s97 (6C/§2.4): section-specific helper copy, shown as a line above the record
   *  list when the section needs attention / isn't started. `undefined` → no line. */
  helperText?: string;
  /** Row ids to render, already filtered (hidden rows excluded) by the parent. */
  rowIds: string[];
  readOnly: boolean;
  clientId: string;
  secureFileSetupAt?: Date | null;
  getCellValue: (fieldId: string, rowId: string) => string;
  /** Whether a cell's stored entry is a seeded default (not user-entered). Drives the
   *  3B scaffold suppression — a pure scaffold row never shows the incomplete dot.
   *  Optional: when omitted, every cell is treated as user-entered (no suppression). */
  getCellIsSeeded?: (fieldId: string, rowId: string) => boolean;
  onCellChange: (fieldId: string, rowId: string, value: string) => void;
  onCellBlur: (field: GenericField, rowId: string) => void;
  onAddRow: () => void;
  /** SINGLE-FLIGHT-PRIMITIVE-1 (s170): true while the parent's add-row mutation is in flight.
   *  The parent owns the synchronous interlock (`useSingleFlight`) because it owns the mutation;
   *  this prop is only the visible affordance. Until s170 this button carried NO `disabled` at
   *  all — and since the rowId is a fresh `crypto.randomUUID()` minted inside the mutationFn,
   *  every extra fire is a legitimately-distinct blank record no server backstop can collapse.
   *  This is the shared add control for EVERY multi-record section, so it is the single largest
   *  double-fire surface in the app. */
  addRowDisabled?: boolean;
  onDeleteRow: (rowId: string) => void;
  onHideRow: (rowId: string) => void;
  /** One file → attach AND extract, handled by the parent (approach ii). */
  onUploadFile: (rowId: string, file: File) => void | Promise<void>;
  /** Rows whose async extraction job is still running (session 90) — drives the
   *  "Extracting…" indicator after the upload itself has returned. */
  extractingRowIds?: Set<string>;
  getRowDocCount: (rowId: string) => number;
  documentsByKey: Record<string, PanelDoc[]>;
  onEnsureRowDocs: (rowId: string) => void;
  onDownloadDocument: (docId: string) => void;
  onViewFile: (file: PanelDoc) => void;
  /** s96: open the AI summary for a document (only shown when one exists). */
  onViewSummary?: (doc: PanelDoc) => void;
  onDeleteDocument: (docId: string, rowId: string) => void | Promise<void>;
  /** When set, that row is opened + scrolled into view (used by the parent to
   *  reveal a row whose document arrived out-of-band via the phone/SSE path,
   *  mirroring the in-page upload behavior). The parent clears it via
   *  onRowRevealed once handled. */
  revealRowId?: string | null;
  onRowRevealed?: () => void;
  /** Returns true when a row is locked (seeded/required — e.g. a Gov-IDs cascade
   *  row). On a locked row a lock icon replaces the row-level trash, the delete
   *  action is suppressed (row + drawer), and the FIRST basic field renders
   *  read-only — mirroring the table layout's `isLockedRow` rule. The renderer is
   *  agnostic to WHY a row is locked; the parent supplies the predicate. Defaults
   *  to never-locked, so non-locked sections are unaffected. */
  isRowLocked?: (rowId: string) => boolean;
  /** Persist one field value directly (value-passing), bypassing the
   *  onCellChange→onCellBlur local-edit path. Copy-from-sibling needs this: a
   *  programmatic multi-field copy can't rely on onCellBlur, which reads stale
   *  local-edit state from its closure. Wired by the parent to the same
   *  generic_entries upsert mutation. Optional — copy is config-gated. */
  onSaveFieldValue?: (field: GenericField, rowId: string, value: string) => Promise<unknown> | void;
  /** Optional per-row extra content rendered in the open drawer, immediately after
   *  the grouped fields (i.e. under the last group). Content-agnostic — the parent
   *  supplies the node. Used by the executor section for the "Send invitation"
   *  control (s91, 13a follow-up). Only rendered when not read-only. */
  renderDrawerExtra?: (rowId: string) => ReactNode;
  /** EXECUTOR-WRITE-1 Phase 4R (s144, D-144): make the FIELD INPUTS editable even in a read-only
   *  section (the executor plan view is readOnly but edits the real fields in place). This flips ONLY
   *  the field cells — every structural affordance (add/delete/hide/decline/capture/copy/reorder)
   *  stays gated on `readOnly`, so nothing planner-only leaks. Default false → planner byte-identical.
   *  Cat A cells stay locked regardless (EditableCell's own isCategoryA treatment). */
  fieldsEditableWhenReadOnly?: boolean;
  /** Phase 4R: per-field executor edit provenance. Returns the planner's original value for a field
   *  the executor has overwritten (→ an inline "edited · was …" badge under the input); undefined =
   *  never edited. */
  getFieldEdit?: (fieldId: string, rowId: string) => { originalValue: string | null } | undefined;
  /** Phase 4R: executor row chrome rendered in the open drawer footer (where the planner delete sits)
   *  — remove for a live record, or Save/Cancel for a pending "record you found". Rendered whenever
   *  provided, independent of `readOnly` (it IS the executor's read-only-surface affordance). */
  renderRowActions?: (rowId: string) => ReactNode;
  /** s229 (D-229.102 C2): what an EMPTY section says to a viewer who cannot add to it (the executor's
   *  stage-1 panel). Absent → the planner's "tap Add … scan a document" line, unchanged. */
  emptyNote?: string;
  /** Phase 4R (D-144): SHARED executor-removed-row state (same predicate on BOTH surfaces). A row in
   *  this set renders dimmed/struck with an inline removed line instead of the normal ribbon. */
  removedRowIds?: Set<string>;
  /** Executor-only Undo for a removed row (planner omits it → the removed line reads `removedByLabel`
   *  instead of an Undo button). */
  onUndoRemoval?: (rowId: string) => void;
  /** Planner-surface label for a removed row (e.g. "removed by the executor"); shown when no Undo. */
  removedByLabel?: string;
  /** The record's identifying value (e.g. the bank name) — names the removed line. */
  getRowIdentity?: (rowId: string) => string;
  /** s98 per-field "I don't have this" decline. `getCellDeclined` → whether the
   *  field's entry is declined; `onSetCellDeclined` → toggle it. The affordance lives
   *  in the OPEN DRAWER (the ribbon stays compact); a declined basic field reads
   *  "Not available" on the ribbon. Both optional — when absent (e.g. the standalone
   *  wrapper) no decline UI renders. */
  getCellDeclined?: (fieldId: string, rowId: string) => boolean;
  /** 🔴 s179 (TWO-REASON-DECLINE-1): WHY the cell was declined, for the per-reason status line.
   *  Optional and separate from `getCellDeclined` so a parent that has not adopted reasons keeps
   *  compiling and simply renders the conservative wording. */
  getCellDeclineReason?: (fieldId: string, rowId: string) => DeclineReason | null;
  /** 🔴 s179 rebuild: the planner's location note for a withheld cell. */
  getCellDeclineNote?: (fieldId: string, rowId: string) => string | null;
  onSetCellDeclined?: (field: GenericField, rowId: string, declined: boolean, reason?: DeclineReason, note?: string | null) => void;
  /** OTHER-SPECIFY-1 (s155, D-155.6): per-field "Other → tell us more" detail. `getCellSpecify`
   *  → the field's generic_entries.specify_text; `onSaveSpecify` → persist it (same single-writer
   *  generic-entries POST as the value/decline). The box reveals in the OPEN DRAWER beneath a
   *  specifyOnOther select when its "Other" option is chosen; NON-REQUIRED, never enters completion.
   *  Both optional — absent (e.g. a wrapper that doesn't wire them) → no specify UI. */
  getCellSpecify?: (fieldId: string, rowId: string) => string;
  onSaveSpecify?: (field: GenericField, rowId: string, text: string) => void;
  /** s99 drawer-stability fix (#1): OPTIONAL controlled expanded-row state. When
   *  the parent supplies both, the open/closed state of each record drawer lives in
   *  the parent (which survives this component's remount), so a background refetch
   *  that remounts the layout no longer collapses an open drawer (and no mid-render
   *  remount swallows the first click). Absent (e.g. the standalone wrapper) → the
   *  component keeps its own local state, unchanged. */
  expandedRowIds?: Set<string>;
  onSetRowExpanded?: (rowId: string, open: boolean) => void;
}

// Inline-row grid = chevron | <basic fields, sized from config> | Docs |
// Capture. The basic-field tracks come from config.basicFieldWidths so the
// renderer never hard-codes per-section widths (content-agnostic). The two
// trailing columns are the renderer's own structural slots.
const BASIC_WIDTH_TRACK: Record<BasicFieldWidth, string> = {
  narrow: "80px",
  default: "minmax(110px, 150px)",
  currency: "110px",
  flex: "minmax(160px, 1fr)",
};
// Fixed structural columns following the basic fields. Docs track is wide enough for
// its "Documents" header label (s101: was 56px → "Documents" overflowed into "Upload").
const TRAILING_TRACKS = "76px minmax(210px, 1fr)"; // Docs | Capture

// Upload-icon cluster — shared between the body icons (renderCaptureButtons) and an
// INVISIBLE replica in the ribbon "Upload" header. The header sizes itself to the
// replica so "Upload" centers over the exact icon-cluster footprint, with no magic
// width and regardless of label length or how wide the 1fr Capture column stretches
// (DB-45 1F — replaces 1E's hardcoded w-[140px]). Both the cluster gap and the
// per-stack horizontal padding (which, with the label, sets each stack's width) are
// declared once here so the body and the header replica never drift apart.
const UPLOAD_CLUSTER_CLASS = "flex items-center gap-3";
const UPLOAD_STACK_PAD = "px-1";
const UPLOAD_LABEL_CLASS = "text-[9px] leading-none whitespace-nowrap";
const UPLOAD_PHONE_LABEL = "Upload from phone";
const UPLOAD_FILE_LABEL = "Upload file";
// One source of truth for the two upload-stack labels — drives BOTH the real buttons
// (renderCaptureButtons) and the invisible width-setter below, so they can't drift.
const UPLOAD_LABELS = [UPLOAD_PHONE_LABEL, UPLOAD_FILE_LABEL];

// Non-interactive width-equivalent of the upload-icon cluster: same container + per-
// stack padding + labels as the real buttons (the labels, wider than the 20px icons,
// drive each stack's width). Fed to <HeaderOverContent> so the "Upload" header sizes
// to the EXACT cluster footprint and centers over the icon pair.
function UploadClusterFootprint() {
  return (
    <span className={UPLOAD_CLUSTER_CLASS}>
      {UPLOAD_LABELS.map((label) => (
        <span key={label} className={UPLOAD_STACK_PAD}>
          <span className={UPLOAD_LABEL_CLASS}>{label}</span>
        </span>
      ))}
    </span>
  );
}

// Drawer field column-span → Tailwind class. The drawer is a responsive grid
// (DRAWER_GRID): 1 col (narrow) → 2 (sm) → 4 (lg). Span classes are breakpoint-
// scoped so a span never exceeds the live column count — e.g. a "2" field is one
// full column on the 1-col grid, a full row on the 2-col grid, and half on the
// 4-col grid; nothing overflows. Content-agnostic: spans come from config; a
// field with no hint falls back to span 1.
const DRAWER_GRID = "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-[14px] gap-y-[9px]";
const DRAWER_SPAN_CLASS: Record<DrawerFieldSpan, string> = {
  1: "col-span-1",
  2: "col-span-1 sm:col-span-2",
  full: "col-span-1 sm:col-span-2 lg:col-span-4",
};

export function ExpandableSectionLayout(props: ExpandableSectionLayoutProps) {
  const {
    section,
    config,
    helperText,
    rowIds,
    readOnly,
    emptyNote,
    clientId,
    secureFileSetupAt,
    getCellValue,
    getCellIsSeeded,
    onCellChange,
    onCellBlur,
    onAddRow,
    addRowDisabled,
    onDeleteRow,
    onHideRow,
    onUploadFile,
    extractingRowIds,
    getRowDocCount,
    documentsByKey,
    onEnsureRowDocs,
    onDownloadDocument,
    onViewFile,
    onViewSummary,
    onDeleteDocument,
    revealRowId,
    onRowRevealed,
    isRowLocked,
    onSaveFieldValue,
    renderDrawerExtra,
    fieldsEditableWhenReadOnly,
    getFieldEdit,
    renderRowActions,
    removedRowIds,
    onUndoRemoval,
    removedByLabel,
    getRowIdentity,
    getCellDeclined,
    onSetCellDeclined,
    getCellDeclineReason,
    getCellDeclineNote,
    getCellSpecify,
    onSaveSpecify,
    expandedRowIds,
    onSetRowExpanded,
  } = props;

  // s98: decline UI is active only when the parent wires both callbacks.
  const declineEnabled = !!getCellDeclined && !!onSetCellDeclined;
  const isCellDeclined = (fieldId: string, rowId: string) =>
    declineEnabled && getCellDeclined!(fieldId, rowId);
  // s179: the reason behind a decline, for the per-reason status line. Null when the parent has
  // not wired reasons — the shared `FieldDeclineControl` then renders the conservative wording.
  const cellDeclineReason = (fieldId: string, rowId: string): DeclineReason | null =>
    (declineEnabled && getCellDeclineReason?.(fieldId, rowId)) || null;
  const cellDeclineNote = (fieldId: string, rowId: string): string | null =>
    (declineEnabled && getCellDeclineNote?.(fieldId, rowId)) || null;

  // OTHER-SPECIFY-1 (s155): specify UI is active only when the parent wires both callbacks.
  const specifyEnabled = !!getCellSpecify && !!onSaveSpecify;

  // Per-section copy nouns (config-driven; stored lowercase singular).
  const { recordNoun, recordNounPlural } = config;
  const pluralNoun = recordNounPlural ?? recordNoun + "s";
  /** s158 — this section's lens anchor emitter. Bound once so every cell branch (ribbon:
   *  showIf-hidden / declined / address / normal, plus the drawer) spreads the SAME attributes
   *  and no branch can hand-roll a variant (G7). */
  const lensAnchor = (fieldKey: string, rowId?: string | null) => lensFieldAnchor(section.slug, fieldKey, rowId);

  // Section-header field (s95, Option 2): a single fact rendered above the record
  // list (its value lives on the dedicated `__header__` row). Resolve its field def
  // and drop the header row from the rendered records so it never shows as a card.
  const headerFieldDef = config.headerField
    ? section.fields.find((f) => f.fieldKey === config.headerField!.fieldKey) ?? null
    : null;
  // 🔴 s192 (D-192.1): the exclusion is UNCONDITIONAL. It used to be gated on
  // `config.headerField`, so retiring a header mount turned that section's existing
  // `__header__` rows — created lazily on first save, one cell each — into ordinary
  // record cards. A `__header__` row is a section-level fact and is NEVER a record,
  // in any section, whether or not a mount currently declares one.
  const contentRowIds = rowIds.filter((id) => !isHeaderRow(id));

  // Independent (multi) expand — opening one row never closes another.
  // s99 (#1): controlled when the parent supplies expandedRowIds + onSetRowExpanded
  // (state then survives a remount); else local. `setRowExpanded(rowId, open)` is the
  // single mutation path used by both toggle and the upload-reveal.
  const controlledExpand = expandedRowIds !== undefined && onSetRowExpanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState<Set<string>>(new Set());
  const expanded = controlledExpand ? expandedRowIds! : internalExpanded;
  const setRowExpanded = (rowId: string, open: boolean) => {
    if (controlledExpand) {
      onSetRowExpanded!(rowId, open);
      return;
    }
    setInternalExpanded((prev) => {
      const next = new Set(prev);
      if (open) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  // Inline two-click confirms (brand-consistent — no native popups). A
  // destructive action shows "Remove?/Delete?" + confirm/cancel in place.
  const [confirmingDeleteDocId, setConfirmingDeleteDocId] = useState<string | null>(null);
  const [confirmingDeleteRowId, setConfirmingDeleteRowId] = useState<string | null>(null);

  // Upload-from-computer: one hidden input, row context held in a ref.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadRowId = useRef<string | null>(null);
  const [uploadingRowId, setUploadingRowId] = useState<string | null>(null);
  // Controlled open for the per-row Send-to-Phone dialog. The trigger lives in a clickable
  // row and stops propagation, so it can't rely on the dialog's internal bubble-to-open
  // span — it opens the dialog directly via this state instead.
  const [phoneDialogRowId, setPhoneDialogRowId] = useState<string | null>(null);
  // DB-45 1D: filename per upload row, for the analyzing-banner subtitle (computer
  // uploads only; phone uploads fall back to "your document").
  const [uploadFileNameByRow, setUploadFileNameByRow] = useState<Record<string, string>>({});

  // Copy-from-sibling (config-gated). Per-row picker open state, a pending
  // overwrite confirm ({target,source}), and an in-progress marker.
  const [copyPickerRowId, setCopyPickerRowId] = useState<string | null>(null);
  const [copyConfirm, setCopyConfirm] = useState<{ target: string; source: string } | null>(null);
  const [copyingRowId, setCopyingRowId] = useState<string | null>(null);

  // Per-row card elements, so a just-uploaded row can be scrolled into view.
  const rowRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // When an upload STARTS from a collapsed row, open it and bring it into view
  // so the planner sees their record (and its analyzing/pending/result state)
  // rather than an unchanged collapsed row. No-op if the row is already open
  // (no scroll-jump, no collapse). Multi-expand layout, so other open rows are
  // left untouched. Content-agnostic — purely row-id based.
  const revealRowForUpload = (rowId: string) => {
    if (expanded.has(rowId)) return;
    setRowExpanded(rowId, true);
    onEnsureRowDocs(rowId);
    requestAnimationFrame(() => {
      rowRefs.current.get(rowId)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  // Parent-driven reveal: an out-of-band (phone/SSE) upload completion sets
  // revealRowId on the parent; open + scroll that row, then clear it. Fires only
  // when revealRowId changes; revealRowForUpload is a no-op if already open.
  useEffect(() => {
    if (!revealRowId) return;
    revealRowForUpload(revealRowId);
    onRowRevealed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only on revealRowId change
  }, [revealRowId]);

  const triggerComputerUpload = (rowId: string) => {
    pendingUploadRowId.current = rowId;
    // The upload icon is the ONLY thing that opens the picker. When invoked from a
    // collapsed row, expand it first so the analyzing banner + result render in
    // context; rAF lets the drawer mount before the OS dialog. Already-open rows open
    // the picker immediately.
    if (!expanded.has(rowId)) {
      revealRowForUpload(rowId);
      requestAnimationFrame(() => fileInputRef.current?.click());
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const rowId = pendingUploadRowId.current;
    e.target.value = "";
    pendingUploadRowId.current = null;
    if (!file || !rowId) return;
    // Open + scroll the row on upload START so its result isn't hidden in a
    // collapsed drawer (no-op if already open).
    revealRowForUpload(rowId);
    setUploadFileNameByRow((prev) => ({ ...prev, [rowId]: file.name }));
    setUploadingRowId(rowId);
    try {
      await onUploadFile(rowId, file);
    } finally {
      setUploadingRowId(null);
    }
  };

  const fieldByKey = new Map(section.fields.map((f) => [f.fieldKey, f]));
  const firstFieldId = section.fields[0]?.id;
  const basicFields = config.basicFieldKeys
    .map((k) => fieldByKey.get(k))
    .filter((f): f is GenericField => !!f);
  const notesField = config.notesFieldKey ? fieldByKey.get(config.notesFieldKey) : undefined;
  const docKeyFor = (rowId: string) => entryMapKey(firstFieldId, rowId);

  // s98: every REQUIRED basic field needs a home in the open drawer so its "I don't
  // have this" link has somewhere to live (the ribbon stays compact). Basic fields
  // already placed in a config group keep their spot; ungrouped required basics are
  // injected into the first group (or a synthetic "Details" group when there are no
  // groups). Config-agnostic — no per-section config edits required.
  const groupedFieldKeys = new Set((config.groups ?? []).flatMap((g) => g.fieldKeys));
  // s156 (BENEFICIARY-FILL-1 Layer-3 #3): only DECLINE-ELIGIBLE basics need a drawer home
  // (that's where the "I don't have this" link lives). A non-declineEligible basic
  // (e.g. beneficiary_surname / first_name, children first_name / surname) has no decline link, so
  // injecting it just DUPLICATES the ribbon cell — the ribbon is its sole edit surface.
  //
  // 🔴 s178 (A3, F3): the `isRequired` clause is GONE. It made the drawer home a privilege of
  // required fields, so a declineEligible NON-required basic had no home — and therefore no
  // decline control anywhere, on any surface. Once such a field was declined it could not be
  // undone, and once undone it could not be re-declined: the affordance existed nowhere.
  // Exactly one field is in that class today (`legal-professional-contacts.email`), but the fix
  // is the predicate, not the field — the predicate LIVES in `shared/lib/decline-home.ts` and
  // `decline-home-coverage.test.ts` calls the same function, so a future narrowing clause fails
  // the spec walk instead of quietly re-opening the trap.
  const ungroupedRequiredBasicKeys = declineEnabled
    ? basicFields
        .filter((f) => needsDeclineDrawerHome(f, groupedFieldKeys.has(f.fieldKey)))
        .map((f) => f.fieldKey)
    : [];
  const drawerGroups: { groupName: string; groupNote?: string; fieldKeys: string[] }[] =
    ungroupedRequiredBasicKeys.length === 0
      ? (config.groups ?? [])
      : (config.groups ?? []).length === 0
        ? [{ groupName: "Details", fieldKeys: ungroupedRequiredBasicKeys }]
        : (config.groups ?? []).map((g, i) =>
            i === 0 ? { ...g, fieldKeys: [...ungroupedRequiredBasicKeys, ...g.fieldKeys] } : g,
          );

  // s100: per-row "active" determination — drives the essential-tied × affordance
  // (a shaded row with no data offers decline; one with data offers delete). A row is
  // ACTIVE only when it holds ≥1 field value that is non-empty, non-zero, AND differs
  // from the scaffold's
  // pre-set default — i.e. real user input beyond the seeded type/name. This does NOT
  // rely on is_seeded (which proved unreliable): the pre-set type select value, $0.00,
  // and empty strings never count. Cat A values are unreadable client-side, so those
  // alone fall back to the non-seeded signal; a declined field counts as engagement.
  const sectionDefaultRows =
    (section as { defaultRows?: Array<Record<string, unknown>> | null }).defaultRows ?? [];
  const nonVersionDefaultRows = sectionDefaultRows
    .filter((r) => r != null && (r as Record<string, unknown>).__v === undefined);
  const rowSeedIndex = (rowId: string): number | null => {
    const m = rowId.match(/__(\d+)$/);
    return m ? parseInt(m[1], 10) : null;
  };
  const fieldDefaultValue = (fieldKey: string, rowId: string): string => {
    const i = rowSeedIndex(rowId);
    if (i === null) return "";
    const d = nonVersionDefaultRows[i];
    return d && d[fieldKey] != null ? String(d[fieldKey]).trim() : "";
  };
  // "0", "0.00", "$0.00", "-0.0" → zero (non-empty input required first).
  const isZeroValue = (v: string): boolean => {
    const t = v.replace(/[$,\s]/g, "");
    return t !== "" && /^-?0*\.?0*$/.test(t);
  };
  const rowHasData = (rowId: string): boolean =>
    section.fields.some((f) => {
      if (f.id === headerFieldDef?.id) return false; // header field lives on its own row
      if (isCellDeclined(f.id, rowId)) return true; // "I don't have this" is engagement
      if (isCategoryAField(f)) return getCellIsSeeded?.(f.id, rowId) === false; // unreadable → seeded signal
      const v = (getCellValue(f.id, rowId) ?? "").trim();
      if (v === "" || isZeroValue(v)) return false;
      return v !== fieldDefaultValue(f.fieldKey, rowId);
    });
  // s154 round 2 (item 5, D-154.25): whether a row holds data the PLANNER entered (vs seeded
  // scaffolding). Provenance-based (is_seeded), NOT value-vs-default — the orphan-remap rewrote a
  // seeded site_app value so it no longer matched fieldDefaultValue, so rowHasData wrongly read the
  // pristine Government & Tax scaffold as "started" and stripped its "I don't have this" affordance.
  // A prefilled SEEDED select is not user data; the row stays declinable until the planner types their
  // own value (is_seeded flips false on that cell). Drives ONLY the row-decline eligibility.
  const rowHasUserData = (rowId: string): boolean =>
    section.fields.some((f) => {
      if (f.id === headerFieldDef?.id) return false;
      if (isCellDeclined(f.id, rowId)) return true; // decline is engagement
      if (isCategoryAField(f)) return getCellIsSeeded?.(f.id, rowId) === false; // unreadable → seeded signal
      const v = (getCellValue(f.id, rowId) ?? "").trim();
      if (v === "" || isZeroValue(v)) return false;
      return getCellIsSeeded?.(f.id, rowId) === false; // non-empty AND not seeded scaffolding
    });
  // ── s100 (D-100.x): one-list shaded-critical-rows model ──────────────────────
  // Supersedes the s99 two-zone split + suggestion tray (D-99.4/5/6). Every content
  // row renders as a full ribbon in ONE list. The recommended/critical rows are
  // SHADED (sage-50 + a left accent) and sorted to the top; everything the planner
  // adds renders plain below. A row is critical when it's a locked built-in, an
  // essential-tied scaffold (deleting it would orphan the essential), or a member of
  // the curated TOP_ZONE_SCAFFOLDS set for the section. No "Fill in" prompt — rows
  // show their fields directly; a shaded row resolves by being filled OR declined.
  const sectionTied = sectionHasEssentialRowTie(section.slug);
  const isRowEssentialTied = (rowId: string) =>
    sectionTied &&
    isEssentialTiedRow(section.slug, (fieldKey) => {
      const f = fieldByKey.get(fieldKey);
      return f ? getCellValue(f.id, rowId) : undefined;
    });
  // Curated critical scaffolds — matched on the section's CURATION field (the "type"
  // key = first basic field by default, or a per-section name-field override for
  // sections with no useful type, e.g. Vehicle→make_model, Employment→employer).
  const curationFieldKey = topZoneCurationFieldKey(section.slug, basicFields[0]?.fieldKey);
  const curationFieldId = curationFieldKey ? fieldByKey.get(curationFieldKey)?.id : undefined;
  // Only an actual SEEDED scaffold row can be a curated (shaded) row — match on identity,
  // not value alone. Without this gate a user-added or extraction-created row whose type
  // happens to equal a curated value (e.g. account_type="Chequing") would wrongly shade,
  // contradicting the "user-added rows render plain" intent below (s105 DB-1 follow-up).
  // User/extraction rows always get a random UUID, so isSeededDefaultRow excludes them.
  const matchesTopZoneCuration = (rowId: string) =>
    isSeededDefaultRow(rowId) &&
    // s142 (FIELDFLICKER-1 Fix 4): Career's blank seeded starter is curated by identity, not by
    // a value (its "Current employer" scaffold value was blanked). Every other section still
    // matches on the curation value exactly as before.
    (TOP_ZONE_SEEDED_STARTER_SECTIONS.has(section.slug)
      ? true
      : (curationFieldId ? isTopZoneScaffold(section.slug, getCellValue(curationFieldId, rowId)) : false));
  // s100 (1.7): the curation-field select options that are NOT part of the curated
  // (shaded) set — surfaced as a quiet "Other types you can add" hint beside the Add
  // button instead of as scaffold rows. Empty for sections without a curated set.
  const curationChoices =
    ((curationFieldKey ? (fieldByKey.get(curationFieldKey)?.options as any)?.choices : undefined) as
      | string[]
      | undefined) ?? [];
  const hasCuratedSet = curationChoices.some((c) => isTopZoneScaffold(section.slug, c));
  const otherAddableTypes = hasCuratedSet
    ? curationChoices.filter((c) => !isTopZoneScaffold(section.slug, c))
    : [];
  // SHADED (critical, sorted top): locked, essential-tied, or curated. PLAIN (below):
  // everything else — user-added rows and non-curated scaffolds (e.g. Foreign Bank).
  // Membership is identity-based (locked/tied/curated), NOT data-based, so a shaded
  // row stays shaded whether empty or filled.
  const isShadedRow = (rowId: string) =>
    (isRowLocked?.(rowId) ?? false) ||
    isRowEssentialTied(rowId) ||
    matchesTopZoneCuration(rowId);
  const shadedRowIds = contentRowIds.filter(isShadedRow);
  const plainRowIds = contentRowIds.filter((id) => !isShadedRow(id));
  // Single ordered list: shaded critical rows first, then plain rows, each in their
  // existing relative order.
  const orderedRowIds = [...shadedRowIds, ...plainRowIds];

  // The assessable required fields (ribbon + drawer). Only the section-header field is excluded.
  // 🔴 s185 (D-181.5): the Cat A exclusion is REMOVED. It mirrored a completion carve-out that no
  // longer exists — Cat A is in the denominator now, resolving on user-actioned presence metadata
  // or a decline. Leaving it here would have kept the drawer hint and the section pill disagreeing
  // about the same field: the pill counting a Cat A field the "N more fields →" hint ignored.
  const assessableRequiredFields = section.fields.filter(
    (f) => f.isRequired && f.id !== headerFieldDef?.id,
  );
  // DB-34: ribbon (basic) field ids — a required field NOT in this set lives in the
  // drawer, so a collapsed row with a filled ribbon but empty drawer-required fields
  // can still nudge "N more fields →".
  const basicFieldIds = new Set(basicFields.map((f) => f.id));
  const requiredEmptyOnRow = (f: GenericField, rowId: string) =>
    !isCellDeclined(f.id, rowId) && (getCellValue(f.id, rowId) ?? "").trim() === "";

  // 🔴 s159 (one record, one number) — the collapsed-row hint asks the SHARED completion math what a
  // row still owes, instead of keeping its own tally. `outstandingRequiredInRow` is the same function
  // the D-156 bar resolves each row with, so the ribbon's number and the row's green check can never
  // disagree. It also brings the per-row showIf exclusion the local tally never had (a required field
  // behind a CLOSED gate is not work owed) — the latent over-count fixed here.
  //
  // Fields are mapped into the shared shape ONCE; the header field is excluded because it is a
  // section-level fact stored on the `__header__` row, not something this record owes.
  const progressFields: SectionProgressField[] = section.fields
    .filter((f) => f.id !== headerFieldDef?.id)
    .map((f) => ({
      fieldKey: f.fieldKey,
      isRequired: !!f.isRequired,
      isCategoryA: isCategoryAField(f),
      fieldType: f.fieldType ?? "",
      severityOptional: isSeverityOptionalField(section.slug, f.fieldKey),
    }));
  const basicFieldKeys = new Set(basicFields.map((f) => f.fieldKey));
  /** This row's cells + its per-row hidden set, in the shared math's shape. */
  const progressRowFor = (rowId: string, showIfEntries: ShowIfEntryMap) => ({
    entries: section.fields.map((f) => ({
      fieldKey: f.fieldKey,
      value: getCellValue(f.id, rowId) ?? "",
      isSeeded: getCellIsSeeded?.(f.id, rowId) ?? false,
      declined: isCellDeclined(f.id, rowId),
    })),
    hiddenFieldKeys: section.fields
      .filter((f) => f.conditions && !evaluateShowIf((f.conditions ?? null) as ShowIfCondition | null, showIfEntries))
      .map((f) => f.fieldKey),
  });
  /** Unfilled OPTIONAL drawer fields — what "Optional details <ArrowRight className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" />" offers once nothing is owed.
   *  Same exclusions as the required side (Cat A unreadable, hidden-by-gate not offered). */
  const optionalDrawerRemaining = (rowId: string, hiddenFieldKeys: string[]) => {
    const hidden = new Set(hiddenFieldKeys);
    return section.fields.filter(
      (f) =>
        !basicFieldKeys.has(f.fieldKey) &&
        f.id !== headerFieldDef?.id &&
        !isCategoryAField(f) &&
        !hidden.has(f.fieldKey) &&
        (!f.isRequired || isSeverityOptionalField(section.slug, f.fieldKey)) &&
        requiredEmptyOnRow(f, rowId),
    ).length;
  };
  // Decline / un-decline every assessable required field on a row. Declining is the
  // inline "I don't have this" on a shaded row (and the essential-tied × ): the row
  // resolves as "Not applicable" without being deleted, so an essential is never
  // orphaned. No-op when decline isn't wired.
  const declineRow = (rowId: string) => {
    if (!declineEnabled) return;
    assessableRequiredFields.forEach((f) => onSetCellDeclined!(f, rowId, true));
  };
  const undeclineRow = (rowId: string) => {
    if (!declineEnabled) return;
    assessableRequiredFields.forEach((f) => onSetCellDeclined!(f, rowId, false));
  };
  // A row reads as "Not applicable" when every assessable required field is declined
  // (and there is ≥1 such field). Drives the inline "Not applicable · undo" state.
  const isRowDeclined = (rowId: string) =>
    declineEnabled &&
    assessableRequiredFields.length > 0 &&
    assessableRequiredFields.every((f) => isCellDeclined(f.id, rowId));

  // s102 (DB-21): shaded-section guidance line — replaces the s99 gold-star essential
  // hint (the shaded "recommended" rows + the category-level key already convey
  // "essential", so the star was redundant). Shown in the expanded body while the
  // section still has shaded rows that aren't all RESOLVED (filled OR declined), and
  // never in a read-only (executor / advisor) view. Keyed purely on shaded-row
  // resolution — NOT on ESSENTIAL_CHECKS. Sections with no shaded rows
  // (engaged-mode / plain) fall through to their existing helperText.
  const shadedAllResolved =
    shadedRowIds.length > 0 &&
    shadedRowIds.every((rid) => rowHasData(rid) || isRowDeclined(rid));
  const showShadedGuidance = !readOnly && shadedRowIds.length > 0 && !shadedAllResolved;

  // ── Copy-from-sibling (config-gated; e.g. Guardianship) ──────────────────────
  // One-time SNAPSHOT copy of configured fields from a chosen sibling row into the
  // current row — never `name`/`notes` (the config excludes them). Persists via
  // the value-passing onSaveFieldValue so it doesn't depend on the blur path.
  const copyCfg = config.copyFromSibling;
  const copyFieldDefs = copyCfg
    ? copyCfg.fields.map((k) => fieldByKey.get(k)).filter((f): f is GenericField => !!f)
    : [];
  const rowName = (rid: string) => getCellValue(firstFieldId, rid);

  const runCopy = async (targetRowId: string, sourceRowId: string) => {
    if (!copyFieldDefs.length) return;
    setCopyConfirm(null);
    setCopyPickerRowId(null);
    setCopyingRowId(targetRowId);
    try {
      // Sequential + awaited so none of the writes drop.
      for (const f of copyFieldDefs) {
        const val = getCellValue(f.id, sourceRowId);
        onCellChange(f.id, targetRowId, val); // optimistic UI
        await onSaveFieldValue?.(f, targetRowId, val); // durable, value-passing
      }
    } finally {
      setCopyingRowId(null);
    }
  };

  const requestCopy = (targetRowId: string, sourceRowId: string) => {
    // Overwrite guard: any of the copy-target fields already filled → confirm first.
    const targetFilled = copyFieldDefs.some((f) => getCellValue(f.id, targetRowId).trim() !== "");
    if (targetFilled) {
      setCopyPickerRowId(null);
      setCopyConfirm({ target: targetRowId, source: sourceRowId });
    } else {
      runCopy(targetRowId, sourceRowId);
    }
  };

  // Inline-row grid, built from config so widths are never hard-coded per
  // section: chevron + one track per basic field (its configured width, or
  // "default") + the structural trailing columns.
  const gridTemplate = [
    "32px",
    ...basicFields.map(
      (f) => BASIC_WIDTH_TRACK[config.basicFieldWidths?.[f.fieldKey] ?? "default"],
    ),
    TRAILING_TRACKS,
  ].join(" ");

  const toggle = (rowId: string) => {
    const willOpen = !expanded.has(rowId);
    setRowExpanded(rowId, willOpen);
    // Load documents as a side effect AFTER the state update — never inside the
    // updater (that would trigger a parent state update during render).
    // Expanding a row (chevron / name / any field) NEVER opens the file picker —
    // only the upload icon does (see triggerComputerUpload). DB-45 1D fixed the 1C
    // regression where every expand popped the OS dialog.
    if (willOpen) onEnsureRowDocs(rowId);
  };

  // Neutralizes EditableCell's own muted/bordered box so the field input sits
  // transparently inside the drawer's white field-box wrapper (Input/Textarea
  // are cn()/tailwind-merge based, so these win over the base classes). Value
  // text → 13px charcoal per the brand spec.
  const DRAWER_INPUT =
    "border-0 bg-transparent shadow-none rounded-none hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 text-[13px]";

  // s154 round 2 (item 2): a 2–4-option select renders as RadioOptions in the drawer (EditableCell's
  // !compact radio branch). Radios must sit UNBOXED on the drawer background — the same treatment as
  // the card (organ-donor) path — not inside the white bordered field-box that text inputs use. This
  // predicate decides whether the drawer field gets the box wrapper; it MUST match EditableCell's
  // radio condition (options 2–4, non-compact).
  const rendersAsDrawerRadio = (f: GenericField): boolean => {
    const opts = (f.options as { choices?: string[] } | null)?.choices;
    return Array.isArray(opts) && opts.length >= 2 && opts.length <= 4;
  };

  // `compact` collapses a multiline field to single-row height (rows={1} → the
  // min-h-[32px] floor) so a multiline BASIC field aligns with the 32px input
  // cells in the collapsed row — the s64 EditableCell table fix, applied here too.
  // Only the collapsed-row call passes compact; the drawer keeps full multiline
  // editing. No effect on non-multiline/non-address types (EditableCell ignores it).
  // Phase 4R (D-144): field cells are editable for the executor plan view even though the section is
  // readOnly — `fieldsEditableWhenReadOnly` flips ONLY the inputs; all structural affordances keep
  // their own `readOnly` gate. Cat A stays locked via EditableCell's isCategoryA treatment.
  const cellsReadOnly = readOnly && !fieldsEditableWhenReadOnly;
  // 🔴 Cat A stays LOCKED even when field editing is enabled for the executor: EditableCell's editable
  // secure-field path belongs to the planner's own Secure File (s227: the session provider now sits at the app root,
  // so the crash this once prevented is gone, but the executor still never types into the planner's file). So Cat A is read-only
  // there (renders the provider-free "in the secure file" signpost). The planner path is unaffected
  // (fieldsEditableWhenReadOnly is off → the normal readOnly logic applies).
  const catALockedForExecutor = (field: GenericField) => !!fieldsEditableWhenReadOnly && isCategoryAField(field);
  // F4 (Phase 4R): a Cat A cell on the executor surface renders the secure-file POINTER (states its
  // meaning) — not a dead dash-input. Reuses the same "🔒 in the secure file" treatment as the task
  // drawer (RecordRow/EstatePlanZone). Scoped to the executor plan view via catALockedForExecutor.
  const secureFilePointer = (
    <span className="inline-flex items-center gap-1 text-xs text-warm-taupe italic">
      <Lock className="h-3 w-3" /> in the secure file
    </span>
  );
  const renderCell = (field: GenericField, rowId: string, className?: string, forceReadOnly = false, compact = false, placeholderOverride?: string) => {
    if (catALockedForExecutor(field)) return secureFilePointer;
    const options = (field.options as any)?.choices as string[] | undefined;
    const multiline = field.fieldType === "multiline" || field.fieldType === "notes";
    return (
      <EditableCell
        value={getCellValue(field.id, rowId)}
        onChange={(v) => onCellChange(field.id, rowId, v)}
        onBlur={() => onCellBlur(field, rowId)}
        type={deriveCellInputType(field.fieldType)}
        options={options}
        multiline={multiline}
        placeholder={placeholderOverride ?? field.placeholder ?? undefined}
        readOnly={cellsReadOnly || forceReadOnly || catALockedForExecutor(field)}
        ariaLabel={field.name}
        isCategoryA={isCategoryAField(field)}
        field={field}
        clientId={clientId}
        rowId={rowId}
        secureFileSetupAt={secureFileSetupAt}
        showBorder={false}
        compact={compact}
        className={className}
      />
    );
  };

  // DB-45 1D: a phone outline with an upload arrow on its screen, so the affordance
  // reads as "send to your phone to upload" rather than "call". 20px keeps the
  // icon+label stack inside the dense collapsed-row height (~32px).
  const phoneUploadIcon = (
    <svg
      width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke={SAGE[600]} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="5" y="1" width="14" height="22" rx="3" />
      <line x1="12" y1="17" x2="12" y2="17.01" strokeWidth="2.5" />
      <path d="M12 14 L12 7" />
      <path d="M9 10 L12 7 L15 10" />
    </svg>
  );

  // 🔴 A plain render FUNCTION, deliberately not a component (s168).
  //
  // This was `const CaptureButtons = ({rowId}) => …` used as `<CaptureButtons/>`. A component
  // declared inside a render body gets a NEW type identity on every parent render, so React
  // unmounted and remounted the whole subtree — including the SendToPhoneDialog below — on
  // any re-render. The dialog's open-effect keys off a false→true transition of `open`, and a
  // fresh instance starts with prevOpen=false while the controlled `open` is still true, so
  // each remount re-fired it: a second upload token per dialog, and the dialog resetting to
  // its initial state after Confirm & Save (which invalidates queries → re-render → remount),
  // which read as the dialog reopening by itself.
  //
  // Calling it inlines the JSX into THIS component's tree, so there is no child identity to
  // change and nothing to remount. Do not turn it back into a component.
  const renderCaptureButtons = (rowId: string) => (
    <div className={UPLOAD_CLUSTER_CLASS}>
      <SendToPhoneDialog
        clientId={clientId}
        targetType="generic_section"
        targetId={section.id}
        targetName={section.name}
        rowId={rowId}
        rowLabel={getCellValue(firstFieldId, rowId) || undefined}
        open={phoneDialogRowId === rowId}
        onOpenChange={(o) => setPhoneDialogRowId(o ? rowId : null)}
        trigger={
          <button
            type="button"
            aria-label={UPLOAD_PHONE_LABEL}
            title="Upload from phone — get a link to snap a photo and upload"
            onClick={(e) => { e.stopPropagation(); setPhoneDialogRowId(rowId); }}
            className={`flex flex-col items-center gap-0.5 rounded ${UPLOAD_STACK_PAD} py-0.5 hover:bg-primary/10 transition-colors`}
          >
            {phoneUploadIcon}
            <span className={UPLOAD_LABEL_CLASS} style={{ color: SAGE[600] }}>
              {UPLOAD_PHONE_LABEL}
            </span>
          </button>
        }
      />
      <button
        type="button"
        aria-label="Upload a document from this device"
        title="Upload a document from this device"
        disabled={uploadingRowId === rowId}
        onClick={(e) => { e.stopPropagation(); triggerComputerUpload(rowId); }}
        className={`flex flex-col items-center gap-0.5 rounded ${UPLOAD_STACK_PAD} py-0.5 hover:bg-primary/10 transition-colors disabled:opacity-50`}
      >
        {uploadingRowId === rowId ? (
          <Loader2 className="h-5 w-5 animate-spin" style={{ color: SAGE[600] }} />
        ) : (
          <Paperclip className="h-5 w-5" style={{ color: SAGE[600] }} />
        )}
        <span className={UPLOAD_LABEL_CLASS} style={{ color: SAGE[600] }}>
          {UPLOAD_FILE_LABEL}
        </span>
      </button>
    </div>
  );

  return (
    <div className="p-3">
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={DOCUMENT_ACCEPT}
        onChange={handleFileInputChange}
      />

      {/* Section-header field (s95, Option 2) — one prominent fact above the record
          list (e.g. Organ Donor Status). Bound to the dedicated `__header__` row. */}
      {headerFieldDef && (() => {
        const hRowId = headerRowId(section.slug);
        const options = (headerFieldDef.options as any)?.choices as string[] | undefined;
        return (
          <div className="mb-4 rounded-lg border border-warm-stone bg-[#F7F5F0] px-4 py-3">
            <div className="flex items-center mb-1">
              <span className="text-xs font-semibold text-foreground">
                {config.headerField!.label ?? headerFieldDef.name}
                {fieldRendersStar(section.slug, headerFieldDef) && <RequiredStar />}
              </span>
            </div>
            {config.headerField!.helpText && (
              <p className="text-[11px] text-warm-taupe mb-2 leading-snug">{config.headerField!.helpText}</p>
            )}
            <div className="max-w-xs">
              {!isCellDeclined(headerFieldDef.id, hRowId) && (
                <EditableCell
                  value={getCellValue(headerFieldDef.id, hRowId)}
                  onChange={(v) => onCellChange(headerFieldDef.id, hRowId, v)}
                  onBlur={() => onCellBlur(headerFieldDef, hRowId)}
                  type={deriveCellInputType(headerFieldDef.fieldType)}
                  options={options}
                  placeholder={headerFieldDef.placeholder ?? undefined}
                  readOnly={readOnly}
                  ariaLabel={headerFieldDef.name}
                  isCategoryA={isCategoryAField(headerFieldDef)}
                  field={headerFieldDef}
                  clientId={clientId}
                  rowId={hRowId}
                  secureFileSetupAt={secureFileSetupAt}
                  showBorder={true}
                />
              )}
              {declineEnabled && (
                <FieldDeclineControl
                  declined={isCellDeclined(headerFieldDef.id, hRowId)}
                  declineReason={cellDeclineReason(headerFieldDef.id, hRowId)}
                  declineNote={cellDeclineNote(headerFieldDef.id, hRowId)}
                  eligible={!!headerFieldDef.declineEligible /* D-152.8: declineEligible drives it; Cat-A eligible → presence metadata */}
                  hasValue={!!getCellValue(headerFieldDef.id, hRowId)}
                  readOnly={readOnly}
                  onDecline={(reason, note) => onSetCellDeclined!(headerFieldDef, hRowId, true, reason, note)}
                  onUndo={() => onSetCellDeclined!(headerFieldDef, hRowId, false)}
                  testId={`decline-${headerFieldDef.id}-header`}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* s102 (DB-21): shaded-section guidance band takes priority over the generic
          helper copy while the section has unresolved shaded rows; otherwise the s97
          (§2.4) section-specific helper (shown only when the parent passes it).
          🔴 s177 (A1, D-177.4): the copy no longer says "shaded rows are recommended for
          everyone" — after the suggestion-row retirement there ARE no seeded shaded rows to
          recommend, so that sentence became false the moment the migration ran. The band
          itself stays for the sections that still curate rows (the five password-log
          checklists, D-177.9) and for user-added rows that shade. */}
      {showShadedGuidance ? (
        <p
          className="flex items-center gap-1.5 text-[13px] font-medium"
          style={{ padding: "10px 16px", color: SAGE[600], backgroundColor: SAGE[50], borderLeft: `3px solid ${SAGE[600]}`, borderBottom: `0.5px solid ${WARM.stoneLight}` }}
          data-testid={`shaded-guidance-${section.slug}`}
        >
          <Info className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Add the ones that apply to you. Anything you add here is what your executor will see.
        </p>
      ) : helperText ? (
        <p
          className="text-[13px] text-[#9B9383]"
          style={{ padding: "10px 16px", borderBottom: `0.5px solid ${WARM.stoneLight}` }}
          data-testid={`expandable-helper-${section.slug}`}
        >
          {helperText}
        </p>
      ) : null}

      {/* Column-label row — shown whenever there's ≥1 record (every row is now a
          full ribbon; there are no prompt-only zones). */}
      {orderedRowIds.length > 0 && (
        <div
          className="grid items-center gap-2 px-[9px] py-1.5 text-xs font-medium text-warm-taupe border-b border-warm-stone"
          style={{ gridTemplateColumns: gridTemplate }}
        >
          <span />
          {/* s158: these are column LABELS, not record fields — they carry `data-lens-column` so
              the landing code can never mistake one for a field instance (they used to carry
              `data-lens-field`, which is exactly why a ribbon-field gap highlighted the header
              instead of the record). */}
          {basicFields.map((f) => (
            <span key={f.id} className="truncate" {...lensColumnAnchor(section.slug, f.fieldKey)}>
              {f.name}
              {fieldRendersStar(section.slug, f) && <RequiredStar />}
            </span>
          ))}
          <span>Documents</span>
          {/* The upload-column label, centered over the icon pair via the shared
              HeaderOverContent primitive — the footprint sizes the header to the cluster
              width; a label narrower than the cluster centers within it (DB-45 1F/1I-B). */}
          <HeaderOverContent footprint={<UploadClusterFootprint />}>Supporting Documents</HeaderOverContent>
        </div>
      )}

      {contentRowIds.length === 0 && (
        <p className="py-4 px-2 text-center text-sm text-muted-foreground" data-testid="expandable-empty-note">
          {emptyNote ?? <>No {pluralNoun} yet — tap <strong>Add</strong> to enter one, or scan a document with your phone.</>}
        </p>
      )}

      {orderedRowIds.map((rowId) => {
        // F2 (Phase 4R, D-144): a row the executor removed renders as a dimmed/struck line with an
        // inline removed message — SAME predicate on both surfaces. The executor gets an Undo; the
        // planner reads `removedByLabel` (state only, no action). Replaces the normal ribbon entirely.
        if (removedRowIds?.has(rowId)) {
          const name = (getRowIdentity?.(rowId) ?? "").trim();
          return (
            <div
              key={rowId}
              className="mb-2.5 flex items-center gap-3 rounded-xl border border-warm-stone bg-warm-white px-3 py-2 opacity-60"
              data-testid={`exec-row-removed-${rowId}`}
            >
              <span className="text-xs italic text-warm-taupe line-through">
                {name !== "" ? name : "This record"}
              </span>
              <span className="text-xs italic text-warm-taupe">— removed from the estate</span>
              {onUndoRemoval ? (
                <button
                  type="button"
                  onClick={() => onUndoRemoval(rowId)}
                  className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-sage-600 no-underline"
                  style={{ color: SAGE[600] }}
                  data-testid={`exec-row-removed-undo-${rowId}`}
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Undo
                </button>
              ) : (
                <span className="ml-auto text-xs italic text-warm-taupe" data-testid={`exec-row-removed-by-${rowId}`}>
                  {removedByLabel ?? "removed by the executor"}
                </span>
              )}
            </div>
          );
        }
        const isOpen = expanded.has(rowId);
        const docs = documentsByKey[docKeyFor(rowId)] ?? [];
        const docCount = getRowDocCount(rowId);
        const locked = isRowLocked?.(rowId) ?? false;
        // s100: shaded critical row (sage-50 + left accent) vs plain row. `hasData`
        // (≥1 non-seeded/non-default value) still gates the essential-tied × below.
        const isShaded = isShadedRow(rowId);
        const hasData = rowHasData(rowId);
        // s156 (BENEFICIARY-FILL-1 Layer-3 #2): evaluate ribbon-cell showIf against THIS row's
        // live values so a hidden basic (e.g. beneficiary_first_name on a charity row) reads "—"
        // instead of an empty editable input. Same entry-map shape as the drawer path (DB-29).
        const ribbonShowIf: ShowIfEntryMap = {};
        for (const sf of section.fields) ribbonShowIf[sf.id] = { value: getCellValue(sf.id, rowId) };
        // DB-34: on a collapsed, engaged row whose required RIBBON fields are all
        // filled but whose required DRAWER fields aren't, surface an inline nudge so
        // the planner doesn't read the row as done. Cat A + header excluded (via
        // assessableRequiredFields). Opens the drawer on click.
        // s159: what this row OWES, from the shared completion math (see progressFields above).
        // Split ribbon-vs-drawer only to decide whether to nudge at all — an empty required cell
        // sitting visible in the ribbon needs no hint pointing at the drawer.
        const progressRow = progressRowFor(rowId, ribbonShowIf);
        const outstandingRequired = outstandingRequiredInRow(progressFields, progressRow);
        const ribbonRequiredRemaining = outstandingRequired.filter((f) => basicFieldKeys.has(f.fieldKey)).length;
        const drawerRequiredRemaining = outstandingRequired.length - ribbonRequiredRemaining;
        // The hint speaks only once the ribbon itself reads done (pre-existing precondition, kept):
        // whenever it renders, N === this row's TOTAL outstanding-required count, which is exactly
        // what the bar resolves the row on. A row with zero outstanding can only ever offer the
        // un-numbered optional line — a green-checked row never shows a count.
        // s162 (DRAWER-RENDERER-DEFECTS-1 Defect B) — the hint speaks in read-only mode too.
        //
        // `!readOnly` meant an EXECUTOR (the category page is mounted readOnly for them,
        // ExecutorDashboard.tsx) saw a section header reading "4 need attention" above four rows
        // carrying no indication of which row owed what — they had to open every row to find the
        // four. The count was right; the per-row signal was gated off. `outstandingRequiredInRow`
        // already answers per-row, so nothing new is computed here.
        //
        // Read-only renders the same sentence as STATIC TEXT, not a button: an executor cannot
        // edit the planner's fields, so a click-to-open affordance would promise something the
        // surface does not offer.
        const hintEligible = !isOpen && hasData && ribbonRequiredRemaining === 0;
        const showMoreFieldsNudge = hintEligible && drawerRequiredRemaining > 0;
        // The optional line is an INVITATION to add more ("Optional details <ArrowRight className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" />"), not information.
        // An executor cannot act on it, so it stays suppressed in read-only — unlike the required
        // count above, which is a fact about the plan they are reading.
        const showOptionalHint =
          hintEligible && !readOnly && drawerRequiredRemaining === 0
          && optionalDrawerRemaining(rowId, progressRow.hiddenFieldKeys) > 0;
        return (
          <div
            key={rowId}
            ref={(el) => {
              if (el) rowRefs.current.set(rowId, el);
              else rowRefs.current.delete(rowId);
            }}
            data-shaded={isShaded ? "true" : undefined}
            className={
              isOpen
                // Expanded = active/featured card: 2px sage border, white surface.
                ? "relative mb-2.5 rounded-xl border-2 border-sage-600 bg-warm-white"
                : isShaded
                  // s100: shaded critical row — sage-50 tint + 3px sage left accent
                  // (plain rows stay white; the contrast + the left bar do the separating).
                  ? "relative mb-2.5 rounded-xl border border-warm-stone border-l-[3px] border-l-sage-600 bg-[#F1F5ED]"
                  : "relative mb-2.5 rounded-xl border border-warm-stone bg-warm-white"
            }
          >
            {/* Header row. Expanded card drops 1px of horizontal padding to
                offset its 2px (vs 1px) border, keeping the inline cells aligned
                with collapsed cards. */}
            <div
              className={`grid items-center gap-2 py-1.5 ${isOpen ? "px-[7px]" : "px-2"}`}
              style={{ gridTemplateColumns: gridTemplate }}
            >
              <button
                type="button"
                aria-label={isOpen ? "Collapse record" : "Expand record"}
                aria-expanded={isOpen}
                onClick={() => toggle(rowId)}
                className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground"
              >
                {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {basicFields.map((f) => {
                // s110: on a locked row the section's IDENTITY field is read-only (the
                // seeded identity — e.g. Gov IDs `id_type` — is frozen; all other fields,
                // e.g. `person_name`, stay editable). Keyed on isLockedIdentityField, NOT
                // the positional first field: s108 moved `person_name` to lead the ribbon,
                // so the old `idx === 0` rule froze the wrong field (couldn't name your own
                // IDs). Sections without an identity override keep the first-field freeze.
                const fieldReadOnly = locked && isLockedIdentityField(f.fieldKey, section.fields);
                // Address basic-fields render the compact summary-button in the
                // collapsed row (EditableCell compact mode): a clickable one-line
                // summary that opens the 6-box editor in a popover, same as table
                // cells. The drawer's renderCell still shows the inline structured
                // editor; read-only sections fall through to AddressInput's inline
                // summary (the compact && !readOnly gate inside EditableCell).
                // 3D: required basic-field cells carry the sage left-border accent
                // (matches the column-label border above them).
                const cellClass = "min-w-0";
                // s156 (BENEFICIARY-FILL-1 Layer-3 #2): a basic hidden by showIf on this row
                // reads as a muted dash, never an empty editable input (e.g. first_name on a
                // charity/organization beneficiary).
                if (!evaluateShowIf(f.conditions as ShowIfCondition | null, ribbonShowIf)) {
                  return (
                    <div key={f.id} className={cellClass} {...lensAnchor(f.fieldKey, rowId)}>
                      {/* ACCESSIBLE-NA-LABEL-1 (s195): the dash KEEPS its glyph and pixel
                          footprint; the meaning is carried to assistive tech by an sr-only
                          span so a screen reader announces "Not applicable" instead of
                          "Dash". sr-only is absolutely positioned, so nothing widens. */}
                      <span className="block truncate text-xs" style={{ color: WARM.taupe }}>
                        <span aria-hidden="true">—</span>
                        <span className="sr-only">Not applicable</span>
                      </span>
                    </div>
                  );
                }
                // s156 (Layer-3 #1): beneficiary_surname reads "Organization name" for a
                // charity/organization row, "Surname" for a person. The column header stays the
                // neutral "Surname (or organization name)". Placeholder-level (per-row), ribbon-only.
                const cellPlaceholderOverride =
                  section.slug === "beneficiaries" && f.fieldKey === "beneficiary_surname"
                    ? (["Charity", "Organization"].includes(getCellValue(fieldByKey.get("beneficiary_type")?.id ?? "", rowId))
                        ? "Organization name"
                        : "Surname")
                    : undefined;
                // s178 (A3): a declined basic field reads "— none" on the compact ribbon AND
                // carries its own always-visible undo. It used to render a bare "Not available"
                // with the toggle available only in the open drawer — a state shown on a surface
                // that could not leave it, which is the F3 trap. Same shared control as every
                // other mount, at compact density, so the words come from the shared `FieldDeclineControl`.
                // (s191, D-191.6: this said `decline-copy.ts`, which does not exist.)
                if (isCellDeclined(f.id, rowId)) {
                  return (
                    <div key={f.id} className={cellClass} {...lensAnchor(f.fieldKey, rowId)}>
                      <FieldDeclineControl
                        declined
                        declineReason={cellDeclineReason(f.id, rowId)}
                        declineNote={cellDeclineNote(f.id, rowId)}
                        eligible={!!f.declineEligible}
                        hasValue={false}
                        readOnly={readOnly}
                        density="compact"
                        onDecline={(reason, note) => onSetCellDeclined!(f, rowId, true, reason, note)}
                        onUndo={() => onSetCellDeclined!(f, rowId, false)}
                        testId={`decline-ribbon-${f.id}-${rowId}`}
                      />
                    </div>
                  );
                }
                if (f.fieldType === "address") {
                  return (
                    <div key={f.id} className={cellClass} {...lensAnchor(f.fieldKey, rowId)}>
                      <EditableCell
                        value={getCellValue(f.id, rowId)}
                        onChange={(v) => onCellChange(f.id, rowId, v)}
                        onBlur={() => onCellBlur(f, rowId)}
                        type={deriveCellInputType(f.fieldType)}
                        placeholder={f.placeholder ?? undefined}
                        readOnly={cellsReadOnly || fieldReadOnly || catALockedForExecutor(f)}
                        ariaLabel={f.name}
                        isCategoryA={isCategoryAField(f)}
                        field={f}
                        clientId={clientId}
                        secureFileSetupAt={secureFileSetupAt}
                        compact={true}
                      />
                    </div>
                  );
                }
                return (
                  <div key={f.id} className={cellClass} {...lensAnchor(f.fieldKey, rowId)}>{renderCell(f, rowId, undefined, fieldReadOnly, true, cellPlaceholderOverride)}</div>
                );
              })}
              <span className="text-xs text-muted-foreground text-center">
                {docCount > 0 ? docCount : "—"}
              </span>
              {/* Trailing cluster. Collapsed rows carry a quick row-level
                  delete (list cleanup without expanding each record): reuses
                  onDeleteRow + confirmingDeleteRowId, and on confirm swaps the
                  capture buttons in place for the inline confirm (no popup, no
                  added row width). HARD delete → copy states permanence. When
                  the row is open the drawer footer owns delete, so the header
                  trash is suppressed (avoids a duplicate confirm on the shared
                  state). Trash/confirm buttons stopPropagation so they never
                  toggle the row's expand/collapse. */}
              {!readOnly ? (
                isOpen ? (
                  renderCaptureButtons(rowId)
                ) : locked ? (
                  // Locked row: lock icon stands in for the row-level trash; the
                  // delete handler is unreachable. Upload is still allowed.
                  <div className="flex items-center justify-between gap-1">
                    {renderCaptureButtons(rowId)}
                    <span
                      aria-label={`Locked ${recordNoun} — required, cannot be deleted`}
                      title="Required — cannot be removed"
                      className="flex h-6 w-6 shrink-0 items-center justify-center text-muted-foreground"
                    >
                      <Lock className="h-3.5 w-3.5" style={{ color: "hsl(168 76% 42%)" }} />
                    </span>
                  </div>
                ) : confirmingDeleteRowId === rowId ? (
                  <span className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-xs">
                    <span className="text-muted-foreground">Delete this {recordNoun}? This can't be undone.</span>
                    <button
                      type="button"
                      aria-label={`Confirm delete ${recordNoun}`}
                      className="font-medium text-destructive hover:underline"
                      onClick={(e) => { e.stopPropagation(); setConfirmingDeleteRowId(null); onDeleteRow(rowId); }}
                    >
                      Delete
                    </button>
                    <button
                      type="button"
                      aria-label={`Cancel delete ${recordNoun}`}
                      className="text-muted-foreground hover:underline"
                      onClick={(e) => { e.stopPropagation(); setConfirmingDeleteRowId(null); }}
                    >
                      Cancel
                    </button>
                  </span>
                ) : isShaded ? (
                  // s100: shaded rows are NOT deletable (no trash). The decline link
                  // ("I don't have this") is their only removal-style affordance.
                  // s102 (DB-21): the capture buttons render FIRST so the upload icons pin to
                  // the track start under the "Upload" header — identical x to a plain
                  // row's icons (the decline link previously rendered first and shoved
                  // them ~96px off). The decline link follows to their right.
                  // Empty shaded → decline; declined → "Not applicable · undo"; filled
                  // shaded → uploads only (nothing to remove).
                  <div className="flex items-center gap-2">
                    {renderCaptureButtons(rowId)}
                    {declineEnabled && (isRowDeclined(rowId) || !rowHasUserData(rowId)) && (
                      isRowDeclined(rowId) ? (
                        <span className="inline-flex shrink-0 items-center gap-1.5 text-xs" data-testid={`row-na-${rowId}`}>
                          <span className="italic" style={{ color: WARM.taupe }}>Not applicable</span>
                          <button
                            type="button"
                            aria-label={`Undo not applicable for ${recordNoun}`}
                            onClick={(e) => { e.stopPropagation(); undeclineRow(rowId); }}
                            className="font-medium hover:underline"
                            style={{ color: SAGE[600] }}
                          >
                            I have this now
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          aria-label={`Mark ${recordNoun} not applicable`}
                          title="I don't have this"
                          data-testid={`row-decline-${rowId}`}
                          onClick={(e) => { e.stopPropagation(); declineRow(rowId); }}
                          className="shrink-0 text-xs font-medium hover:underline"
                          style={{ color: WARM.taupe }}
                        >
                          I don't have this
                        </button>
                      )
                    )}
                  </div>
                ) : (
                  // Plain (non-shaded) row: uploads + a row-level delete (trash).
                  <div className="flex items-center justify-between gap-1">
                    {renderCaptureButtons(rowId)}
                    <button
                      type="button"
                      aria-label={`Delete ${recordNoun}`}
                      title={`Delete this ${recordNoun}`}
                      onClick={(e) => { e.stopPropagation(); setConfirmingDeleteRowId(rowId); }}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )
              ) : <span />}
            </div>

            {/* F1 (Phase 4R) + change-marker completeness (s145): row-level executor-edit provenance in
                the COLLAPSED list so a fresh login sees which records the executor corrected without
                opening each drawer. Now a COUNT across the FULL field universe (ribbon + drawer + notes)
                via the shared editedFieldIdsOnRow helper — the prior version counted only basicFields, so
                a record with edits outside the ribbon under-reported ("3 edited, 1 shown"). The per-field
                "edited · was …" detail lives in the open drawer (covers notes too). */}
            {getFieldEdit && !isOpen && (() => {
              const count = editedFieldIdsOnRow(section.fields.map((f) => f.id), rowId, getFieldEdit).length;
              if (count === 0) return null;
              return (
                <div className="px-2 pb-1.5 -mt-0.5" data-testid={`exec-row-prov-${rowId}`}>
                  <span className="text-[11px] italic text-warm-taupe">
                    {count} field{count === 1 ? "" : "s"} edited
                  </span>
                </div>
              );
            })()}

            {/* DB-34 → s159: ribbon-to-drawer hint. The ribbon reads done; the drawer still owes
                REQUIRED fields, so the count names work owed rather than "fields that exist".
                N comes from the shared completion math — the same number the bar resolves this
                row on. Click opens the drawer. */}
            {showMoreFieldsNudge && (readOnly ? (
              // s162 Defect B: static in read-only — same fact, no false affordance, no arrow
              // (the arrow reads as "click to go there", which an executor cannot do here).
              <div
                data-testid={`row-more-fields-${rowId}`}
                className="flex w-full items-center gap-1 px-2 pb-1.5 -mt-0.5 text-[11px] font-medium"
                style={{ color: SAGE[600] }}
              >
                {drawerRequiredRemaining} required field{drawerRequiredRemaining === 1 ? "" : "s"} left
              </div>
            ) : (
              <button
                type="button"
                onClick={() => toggle(rowId)}
                data-testid={`row-more-fields-${rowId}`}
                className="flex w-full items-center gap-1 px-2 pb-1.5 -mt-0.5 text-[11px] font-medium hover:underline"
                style={{ color: SAGE[600] }}
              >
                {drawerRequiredRemaining} required field{drawerRequiredRemaining === 1 ? "" : "s"} left <ArrowRight className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" />
              </button>
            ))}
            {/* s159: nothing is OWED, but there is more the planner could add. Muted and
                un-numbered — an invitation, never a debt. */}
            {showOptionalHint && (
              <button
                type="button"
                onClick={() => toggle(rowId)}
                data-testid={`row-optional-details-${rowId}`}
                className="flex w-full items-center gap-1 px-2 pb-1.5 -mt-0.5 text-[11px] text-muted-foreground hover:underline"
              >
                Optional details <ArrowRight className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" />
              </button>
            )}

            {/* Expanded detail — contained on its own inset sage panel (drawer),
                inset from the card edges so it never merges with the card above.
                Depth: card (white) → drawer (sage-50) → field box (white). */}
            {isOpen && (
              <div className="m-[13px] rounded-[8px] bg-sage-50 p-4">
                {/* DB-45 1D: prominent inline analyzing banner — above the fields so the
                    planner sees that their upload is being read. Replaces the old tiny
                    sage text line in the Documents block. Deeper sage (#E8F0E3) pops
                    against the sage-50 drawer. Covers both upload + extraction states. */}
                {(uploadingRowId === rowId || extractingRowIds?.has(rowId)) && (() => {
                  const fname = uploadFileNameByRow[rowId] ?? "your document";
                  const isUploading = uploadingRowId === rowId;
                  return (
                    <div
                      className="mb-3 flex items-center gap-3 rounded-lg px-4 py-3.5"
                      style={{ backgroundColor: "#E8F0E3" }}
                      aria-live="polite"
                      data-testid={`analyzing-banner-${rowId}`}
                    >
                      <span
                        className="h-5 w-5 shrink-0 animate-spin rounded-full"
                        style={{ border: `2.5px solid ${SAGE[600]}`, borderTopColor: "transparent" }}
                        aria-hidden="true"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium" style={{ color: SAGE[800] }}>
                          {isUploading ? "Uploading your document…" : "Analyzing your document…"}
                        </p>
                        <p className="truncate text-xs" style={{ color: SAGE[600] }}>
                          {isUploading
                            ? `Sending ${fname}…`
                            : `We're reading ${fname} and filling your fields.`}
                        </p>
                      </div>
                    </div>
                  );
                })()}
                {/* Copy-from-sibling control (config-gated). Hidden when there's no
                    other record to copy from. Picker → optional overwrite confirm. */}
                {copyCfg && !readOnly && (() => {
                  const others = contentRowIds.filter((r) => r !== rowId);
                  if (others.length === 0) return null;
                  const busy = copyingRowId === rowId;
                  return (
                    <div className="mb-3">
                      {copyConfirm?.target === rowId ? (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                          <span className="text-warm-taupe">
                            Replace {rowName(rowId) || "this record"}'s current details?
                          </span>
                          <button type="button" className="font-medium text-[#4F6B51] hover:underline"
                            onClick={() => runCopy(copyConfirm.target, copyConfirm.source)}>
                            Replace
                          </button>
                          <button type="button" className="text-muted-foreground hover:underline"
                            onClick={() => setCopyConfirm(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : copyPickerRowId === rowId ? (
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                          <span className="text-warm-taupe">Copy from:</span>
                          {others.map((srcId) => (
                            <button key={srcId} type="button" className="font-medium text-[#4F6B51] hover:underline"
                              onClick={() => requestCopy(rowId, srcId)}>
                              {rowName(srcId) || "Unnamed"}
                            </button>
                          ))}
                          <button type="button" className="text-muted-foreground hover:underline"
                            onClick={() => setCopyPickerRowId(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button type="button" disabled={busy}
                          className="text-[13px] font-medium text-[#4F6B51] hover:underline disabled:opacity-50"
                          onClick={() => setCopyPickerRowId(rowId)}>
                          {busy ? "Copying…" : copyCfg.label}
                        </button>
                      )}
                    </div>
                  );
                })()}
                {/* OTHER-SPECIFY-1 (s155, D-155.6): a BASIC (ribbon) specifyOnOther field's "Other" detail
                    can't live on the compact ribbon, so — like the basic-field decline control — its "Tell
                    us more" box lives in the OPEN DRAWER. A basic field ALREADY re-rendered in the drawer
                    (required basics injected into drawerGroups via ungroupedRequiredBasicKeys) carries its
                    own inline box below the field, so exclude those here to avoid a double box; this block
                    covers the remaining basics (non-required, or decline disabled) that the drawer omits. */}
                {specifyEnabled && (() => {
                  const drawerRenderedKeys = new Set(drawerGroups.flatMap((g) => g.fieldKeys));
                  return basicFields
                    .filter((f) => f.specifyOnOther && !drawerRenderedKeys.has(f.fieldKey)
                      && !isCellDeclined(f.id, rowId) && isOtherOption(getCellValue(f.id, rowId)))
                    .map((f) => (
                    <div key={`specify-basic-${f.id}`} className="mb-3 flex flex-col gap-1">
                      <span className="text-xs text-warm-taupe">{f.name} — “Other” detail</span>
                      <SpecifyOnOtherInput
                        specifyOnOther={f.specifyOnOther}
                        value={getCellValue(f.id, rowId)}
                        specifyText={getCellSpecify!(f.id, rowId)}
                        onSave={(text) => onSaveSpecify!(f, rowId, text)}
                        readOnly={readOnly}
                        testId={`specify-${f.id}-${rowId}`}
                      />
                    </div>
                  ));
                })()}
                {drawerGroups.map((group, gIdx) => {
                  // DB-29: evaluate each field's `showIf` against THIS row's live
                  // values, so a conditional drawer field (e.g. joint_owner_name)
                  // shows/hides exactly as it does in the card/table paths. The
                  // condition's fieldId targets a sibling in this section, so key
                  // the map by every section field's id.
                  const rowShowIfEntries: ShowIfEntryMap = {};
                  for (const sf of section.fields) {
                    rowShowIfEntries[sf.id] = { value: getCellValue(sf.id, rowId) };
                  }
                  const groupFields = group.fieldKeys
                    .map((k) => fieldByKey.get(k))
                    .filter((f): f is GenericField => !!f)
                    .filter((f) => evaluateShowIf(f.conditions as ShowIfCondition | null, rowShowIfEntries));
                  if (groupFields.length === 0) return null;
                  // The per-row drawer extra (e.g. the executor "Send invitation" control)
                  // rides inside the LAST group's grid so it sits inline beside that group's
                  // inputs — for the executor section that's "Access & Permissions", filling
                  // the empty columns next to the Access Level dropdown instead of stacking
                  // below. Config-agnostic: only sections that pass renderDrawerExtra get it.
                  const showExtra = gIdx === drawerGroups.length - 1 && !readOnly && !!renderDrawerExtra;
                  return (
                    <div key={group.groupName} className="mb-3">
                      <h4 className="text-[11px] font-medium uppercase tracking-[0.08em] text-warm-taupe mb-2">
                        {group.groupName}
                      </h4>
                      {group.groupNote && (
                        <p className="text-[13px] leading-snug text-warm-taupe mb-2 -mt-1">
                          {group.groupNote}
                        </p>
                      )}
                      {/* Responsive column-span grid: fields occupy whole cells
                          (span from config) and align on a shared grid. The label
                          text reserves a fixed two-line height (min-h-[32px]) so
                          every input box in a row starts at the same Y even when a
                          label wraps. */}
                      <div className={DRAWER_GRID}>
                        {groupFields.map((f) => {
                          const fDeclined = isCellDeclined(f.id, rowId);
                          return (
                          <div
                            key={f.id}
                            // PLANNER-LENS-1 (s152): per-field landing anchor for a DRAWER field.
                            // s158: emitted by the SAME helper the ribbon cells use, and it now
                            // carries the rowId — the gap card's click-through opens this record's
                            // drawer, then scrolls+tints THIS record's node.
                            {...lensAnchor(f.fieldKey, rowId)}
                            // DB-37: a showIf-conditional field eases in (fade + slide)
                            // when its condition flips, instead of snapping into place and
                            // jarring the drawer. Non-conditional fields are unaffected.
                            // s154 round 3 (item 2): a radio field takes the FULL drawer width (its own
                            // line) so 2–4 options wrap evenly instead of stranding the 4th on its own
                            // line (the "Guaranteed" 3+1 bug). Non-radio fields keep their config span.
                            className={`flex flex-col gap-1 ${rendersAsDrawerRadio(f) ? DRAWER_SPAN_CLASS.full : DRAWER_SPAN_CLASS[config.fieldSpans?.[f.fieldKey] ?? 1]} ${
                              f.conditions ? "animate-in fade-in slide-in-from-top-1 duration-200" : ""
                            }`}
                          >
                            {/* Label wraps ONLY the field name + input. The decline control
                                must NOT live inside the <label>, or a click on it is forwarded
                                to the labeled input instead of activating the button (s98). */}
                            <label className="flex flex-col gap-1">
                              <span className="text-xs text-warm-taupe min-h-[32px]">
                                {f.name}
                                {fieldRendersStar(section.slug, f) && <RequiredStar />}
                              </span>
                              {!fDeclined && (
                                rendersAsDrawerRadio(f)
                                  // Radios sit unboxed on the drawer background (card parity, item 2) —
                                  // no field-box, and no DRAWER_INPUT (they keep RadioOptions' native text-sm).
                                  ? <div className="pt-0.5">{renderCell(f, rowId, undefined)}</div>
                                  : (
                                    <div className="rounded-md border border-warm-stone bg-warm-white focus-within:ring-1 focus-within:ring-sage-600/40">
                                      {renderCell(f, rowId, DRAWER_INPUT)}
                                    </div>
                                  )
                              )}
                            </label>
                            {/* OTHER-SPECIFY-1 (s155, D-155.6): "Other" → non-required "Tell us more" box.
                                Self-gates on specifyOnOther + an Other value; persists to specify_text;
                                never enters completion. Sibling of the label (like the decline control). */}
                            {specifyEnabled && !fDeclined && (
                              <SpecifyOnOtherInput
                                specifyOnOther={f.specifyOnOther}
                                value={getCellValue(f.id, rowId)}
                                specifyText={getCellSpecify!(f.id, rowId)}
                                onSave={(text) => onSaveSpecify!(f, rowId, text)}
                                readOnly={readOnly}
                                testId={`specify-${f.id}-${rowId}`}
                              />
                            )}
                            {/* Phase 4R: inline executor edit provenance — "edited · was …" under the field. */}
                            {(() => {
                              const edit = getFieldEdit?.(f.id, rowId);
                              if (!edit) return null;
                              const was = (edit.originalValue ?? "").trim();
                              return (
                                <span className="text-[11px] italic text-warm-taupe" data-testid={`exec-field-prov-${f.fieldKey}`}>
                                  edited · was {was !== "" ? was : "blank"}
                                </span>
                              );
                            })()}
                            {/* s98: "I don't have this" / "Not available" — sibling of the label */}
                            {declineEnabled && (
                              <FieldDeclineControl
                                declined={fDeclined}
                                declineReason={cellDeclineReason(f.id, rowId)}
                        declineNote={cellDeclineNote(f.id, rowId)}
                                eligible={!!f.declineEligible /* D-152.8: declineEligible drives it; Cat-A eligible → presence metadata */}
                                hasValue={!!getCellValue(f.id, rowId)}
                                readOnly={readOnly}
                                onDecline={(reason, note) => onSetCellDeclined!(f, rowId, true, reason, note)}
                                onUndo={() => onSetCellDeclined!(f, rowId, false)}
                                testId={`decline-${f.id}-${rowId}`}
                              />
                            )}
                            {!fDeclined && config.valueHelp?.[f.fieldKey]?.[getCellValue(f.id, rowId)] && (
                              <span className="text-[11px] leading-snug text-warm-taupe mt-0.5">
                                {config.valueHelp[f.fieldKey][getCellValue(f.id, rowId)]}
                              </span>
                            )}
                          </div>
                          );
                        })}
                        {showExtra && (
                          // Mirror the field-cell shape (label-height spacer + control row)
                          // so the control lines up with the inputs, and span the remaining
                          // columns so it fills the dead space beside a lone dropdown.
                          <div className="flex flex-col gap-1 col-span-1 sm:col-span-1 lg:col-span-3">
                            <span className="min-h-[32px]" aria-hidden="true" />
                            <div className="flex min-h-[34px] items-center">{renderDrawerExtra!(rowId)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {notesField && (
                  <div className="mb-3">
                    <h4 className="text-[11px] font-medium uppercase tracking-[0.08em] text-warm-taupe mb-2">
                      Notes
                    </h4>
                    <div className="rounded-md border border-warm-stone bg-warm-white focus-within:ring-1 focus-within:ring-sage-600/40">
                      {renderCell(notesField, rowId, DRAWER_INPUT)}
                    </div>
                    {/* change-marker completeness (s145): notes cells now carry the same "edited · was …"
                        provenance marker as regular drawer fields (was omitted from this dedicated block). */}
                    {(() => {
                      const edit = getFieldEdit?.(notesField.id, rowId);
                      if (!edit) return null;
                      const was = (edit.originalValue ?? "").trim();
                      return (
                        <span className="mt-1 block text-[11px] italic text-warm-taupe" data-testid={`exec-field-prov-${notesField.fieldKey}`}>
                          edited · was {was !== "" ? was : "blank"}
                        </span>
                      );
                    })()}
                  </div>
                )}

                {/* Documents — store + list + open/download (Build 1 scope).
                    Heading doubles as the label for the upload controls below
                    (session-61 polish: drawer-only, no caps label on the dense
                    collapsed row). Content-agnostic — correct for any category. */}
                <div className="mb-3">
                  <h4 className="text-[12px] font-medium text-warm-taupe mb-2">
                    Add a document — we'll read it &amp; fill your fields
                  </h4>
                  {!readOnly && <div className="mb-2">{renderCaptureButtons(rowId)}</div>}
                  {/* The analyzing/uploading state shows in the banner at the top of the
                      drawer (DB-45 1D) — not here. */}
                  {docs.length === 0 ? (
                    (uploadingRowId === rowId || extractingRowIds?.has(rowId))
                      ? null
                      : <p className="text-xs text-muted-foreground">No documents attached yet.</p>
                  ) : (
                    <ul className="space-y-1">
                      {docs.map((doc) => (
                        <li
                          key={doc.id}
                          className="flex items-center gap-2 text-sm rounded-md border border-warm-stone bg-warm-white px-2 py-1"
                        >
                          <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <button
                            type="button"
                            className="flex-1 truncate text-left hover:underline"
                            onClick={() => onViewFile(doc)}
                            aria-label={`Open ${doc.filename}`}
                          >
                            {doc.filename}
                          </button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-foreground"
                            aria-label={`Open ${doc.filename}`}
                            title="Open"
                            onClick={() => onViewFile(doc)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-muted-foreground hover:text-foreground"
                            aria-label={`Download ${doc.filename}`}
                            title="Download original"
                            onClick={() => onDownloadDocument(doc.id)}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {/*
                            SUMMARY-CLASS-1 (s217) — rule 120's visibility half.

                            🔴 Two gates, both load-bearing:
                            • `!readOnly` — the EXECUTOR never sees a summary in this panel. It had no
                              readOnly gate at all (the delete button beside it did), so the executor
                              saw every summary the planner did. The executor reads a summary on the
                              to-do that needs the paper, not here; that placement is TASK-LENS's to
                              build — it is deliberately NOT built on this surface.
                            • `documentType === "will"` — the PLANNER sees only the will's summary.
                              Summaries for the other allowed sentence papers (POA, insurance policy,
                              business agreement, pre-paid funeral contract) are still STORED; they are
                              for the executor's to-do, not for this panel.

                            Fail closed: a document with no recorded documentType shows no summary.
                          */}
                          {doc.extractedMetadata?.summary && doc.extractedMetadata?.documentType === "will" && !readOnly && onViewSummary && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-muted-foreground hover:text-foreground"
                              aria-label={`View summary of ${doc.filename}`}
                              title="View AI summary"
                              onClick={() => onViewSummary(doc)}
                            >
                              <FileText className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {!readOnly && (
                            confirmingDeleteDocId === doc.id ? (
                              <span className="flex items-center gap-1.5 shrink-0">
                                <button
                                  type="button"
                                  className="text-[11px] font-medium text-destructive hover:underline"
                                  aria-label={`Confirm remove ${doc.filename}`}
                                  onClick={async () => { setConfirmingDeleteDocId(null); await onDeleteDocument(doc.id, rowId); }}
                                >
                                  Remove
                                </button>
                                <button
                                  type="button"
                                  className="text-[11px] text-muted-foreground hover:underline"
                                  aria-label="Cancel remove"
                                  onClick={() => setConfirmingDeleteDocId(null)}
                                >
                                  Cancel
                                </button>
                              </span>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-muted-foreground hover:text-destructive"
                                aria-label={`Remove ${doc.filename}`}
                                title="Remove"
                                onClick={() => setConfirmingDeleteDocId(doc.id)}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            )
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Panel footer — quiet hide / delete */}
                {!readOnly && (
                  <div className="flex items-center gap-4 pt-2 border-t border-warm-stone">
                    <button
                      type="button"
                      aria-label="Hide from your view"
                      title="Hide from your view — removes this item from your sidebar"
                      onClick={() => onHideRow(rowId)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                      <EyeOff className="h-3.5 w-3.5" />
                      Hide from your view
                    </button>
                    {locked ? (
                      // Locked row: no delete affordance in the drawer either.
                      <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Lock className="h-3.5 w-3.5" style={{ color: "hsl(168 76% 42%)" }} />
                        Required — can't be deleted
                      </span>
                    ) : isShaded ? (
                      // s100: shaded (recommended) rows aren't deletable — use "I don't
                      // have this" on the row instead. No drawer delete.
                      null
                    ) : confirmingDeleteRowId === rowId ? (
                      <span className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground">Delete this {recordNoun}?</span>
                        <button
                          type="button"
                          aria-label={`Confirm delete ${recordNoun}`}
                          className="font-medium text-destructive hover:underline"
                          onClick={() => { setConfirmingDeleteRowId(null); onDeleteRow(rowId); }}
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          aria-label={`Cancel delete ${recordNoun}`}
                          className="text-muted-foreground hover:underline"
                          onClick={() => setConfirmingDeleteRowId(null)}
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Delete ${recordNoun}`}
                        title={`Delete this ${recordNoun}`}
                        onClick={() => setConfirmingDeleteRowId(rowId)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete {recordNoun}
                      </button>
                    )}
                  </div>
                )}
                {/* Phase 4R: executor row chrome (remove/undo, or Save/Cancel for a "record you
                    found") — the read-only-surface equivalent of the planner footer above. */}
                {renderRowActions && !locked && (
                  <div className="flex items-center gap-4 pt-2 border-t border-warm-stone">
                    {renderRowActions(rowId)}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!readOnly && (
        <div className="pt-2 space-y-1">
          {otherAddableTypes.length > 0 && (
            <p className="text-[11px] text-warm-taupe" data-testid={`other-types-${section.slug}`}>
              Other types you can add: {otherAddableTypes.join(" · ")}
            </p>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground"
            onClick={onAddRow}
            disabled={addRowDisabled}
            aria-label={`Add ${recordNoun}`}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add {recordNoun}
          </Button>
        </div>
      )}
    </div>
  );
}
